import { Link } from "react-router-dom";
import type { PriceGroup, PriceRow } from "@/mocks/priceTables";

/**
 * 回数・セット枠をもつグループ用の料金表示。
 * PC: 施術名｜対象・内容｜1回｜5回｜セット の表形式
 * スマホ: 施術名 → 対象・内容 → 各回数の縦ブロック
 *
 * rows を渡した場合はその行のみを表示する（お悩みフィルター適用時など）。
 */
export default function PriceCountTable({ group, rows }: { group: PriceGroup; rows?: PriceRow[] }) {
  const columns = group.columns ?? [];
  const list = rows ?? group.rows;
  const gridTemplate = `1.4fr 1.5fr ${columns.map(() => "96px").join(" ")}`;

  return (
    <>
      {/* PC: 表形式 */}
      <div className="mt-6 hidden md:block">
        <div
          className="grid items-baseline border-t border-foreground-950/20 pb-3 pt-4 text-[11px] tracking-[0.15em] text-foreground-500"
          style={{ gridTemplateColumns: gridTemplate }}
        >
          <span>施術名</span>
          <span>対象・内容</span>
          {columns.map((col) => (
            <span key={col.key} className="text-right">
              {col.label}
            </span>
          ))}
        </div>
        {list.map((row) => (
          <div
            key={`${group.title}-${row.name}`}
            className="grid items-baseline border-t border-foreground-950/12 py-5"
            style={{ gridTemplateColumns: gridTemplate }}
          >
            <span className="pr-6 font-heading text-[15px] text-foreground-950">
              {row.href ? (
                <Link
                  to={row.href}
                  className="group/row inline-flex items-center gap-1.5 underline-offset-4 transition-colors hover:text-secondary-700 hover:underline"
                >
                  {row.name}
                  <i
                    className="ri-arrow-right-line text-sm text-secondary-500 transition-transform group-hover/row:translate-x-0.5"
                    aria-hidden
                  />
                </Link>
              ) : (
                row.name
              )}
            </span>
            <span className="pr-6">
              {row.targetAreas ? (
                <span className="mb-1.5 block border-l border-foreground-950/15 pl-2.5 text-[11.5px] leading-[1.8] text-foreground-500">
                  対象部位：{row.targetAreas}
                </span>
              ) : null}
              <span className="block text-[12.5px] leading-[1.9] text-foreground-600">{row.note}</span>
            </span>
            {columns.map((col) => (
              <span key={col.key} className="text-right font-label text-[15px] text-foreground-900">
                {row.values?.[col.key] ?? "—"}
              </span>
            ))}
          </div>
        ))}
      </div>

      {/* スマホ: 縦ブロック */}
      <div className="mt-6 space-y-3 md:hidden">
        {list.map((row) => (
          <div
            key={`${group.title}-${row.name}-sp`}
            className="border border-foreground-950/12 bg-background-50 p-5"
          >
            <span className="en-eyebrow block text-[9px] text-secondary-500">施術名</span>
            {row.href ? (
              <Link
                to={row.href}
                className="mt-2 inline-flex items-center gap-1.5 font-heading text-[16px] text-foreground-950 underline-offset-4 hover:underline"
              >
                {row.name}
                <i className="ri-arrow-right-line text-sm text-secondary-500" aria-hidden />
              </Link>
            ) : (
              <span className="mt-2 block font-heading text-[16px] text-foreground-950">{row.name}</span>
            )}

            {row.targetAreas ? (
              <span className="mt-3 block border-l border-foreground-950/15 pl-2.5 text-[12px] leading-[1.8] text-foreground-500">
                対象部位：{row.targetAreas}
              </span>
            ) : null}

            <span className="en-eyebrow mt-5 block text-[9px] text-secondary-500">対象・内容</span>
            <span className="mt-2 block text-[12.5px] leading-[1.9] text-foreground-600">{row.note}</span>

            <div className="mt-5 space-y-2.5 border-t border-foreground-950/10 pt-4">
              {columns.map((col) => (
                <div key={col.key} className="flex items-center justify-between">
                  <span className="text-[12.5px] text-foreground-600">{col.label}</span>
                  <span className="font-label text-[15px] text-foreground-900">
                    {row.values?.[col.key] ?? "—"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}